import React, { useState, useEffect } from 'react';
import {
  Globe,
  Lock,
  Plus,
  ChevronDown,
  Copy,
  Check,
  RefreshCw,
  Trash2,
  Clock,
  AlertTriangle,
  CheckCircle,
  Info,
  Loader2,
  X,
} from 'lucide-react';
import { CustomDomainsCoreProps, CustomDomain } from '../widget/types';
import { useWidgetTheme, mainAppTheme } from '../widget/useWidgetTheme';

const CustomDomainsCore: React.FC<CustomDomainsCoreProps> = ({
  domains,
  loading,
  error,
  tierLimit = { current: 0, limit: 0, unlimited: true },
  refreshing = false,
  mode,
  theme: themeOverrides,
  autoExpandIds = [],
  onAddDomain,
  onDeleteDomain,
  onRefresh,
  onSuccess,
  onError,
}) => {
  const widgetTheme = useWidgetTheme();
  const baseTheme = mode === 'modal' ? mainAppTheme : widgetTheme;
  const theme = themeOverrides ? { ...baseTheme, ...themeOverrides } : baseTheme;

  // Local UI state
const [newDomain, setNewDomain] = useState('');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (autoExpandIds.length > 0) {
setExpandedIds(prev => new Set([...Array.from(prev), ...autoExpandIds]));
    }
  }, [autoExpandIds.join(',')]);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  // Delete modal state
  const [deleteModal, setDeleteModal] = useState<{ open: boolean; domain: CustomDomain | null }>({
    open: false,
    domain: null,
  });
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Hover states
  const [addBtnHover, setAddBtnHover] = useState(false);
  const [refreshBtnHover, setRefreshBtnHover] = useState(false);
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);
  const [hoveredButton, setHoveredButton] = useState<string | null>(null);

const handleAddDomain = async () => {
    if (!newDomain.trim()) return;
    setAdding(true);
    setAddError(null);
    try {
      await onAddDomain(newDomain.toLowerCase());
      setNewDomain('');
      setAddError(null);
      onSuccess?.('Domain added! Configure the DNS records shown below.');
    } catch (err: any) {
      const msg = err.message || 'Failed to add domain';
      setAddError(msg);
      onError?.(msg);
    } finally {
      setAdding(false);
    }
  };

  const handleRefresh = async () => {
    try {
      await onRefresh();
      onSuccess?.('Domain status refreshed');
    } catch (err: any) {
      onError?.(err.message || 'Failed to refresh');
    }
  };

  const openDeleteModal = (domain: CustomDomain) => {
    setDeleteModal({ open: true, domain });
    setDeleteConfirmText('');
  };

  const closeDeleteModal = () => {
    setDeleteModal({ open: false, domain: null });
    setDeleteConfirmText('');
    setDeleting(false);
  };

  const handleDeleteDomain = async () => {
    if (!deleteModal.domain) return;
    setDeleting(true);
    try {
      await onDeleteDomain(deleteModal.domain.id);
      onSuccess?.('Domain deleted');
      closeDeleteModal();
    } catch (err: any) {
      onError?.(err.message || 'Failed to delete domain');
      setDeleting(false);
    }
  };

const handleExpandClick = (domainId: number) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(domainId)) {
        next.delete(domainId);
      } else {
        next.add(domainId);
      }
      return next;
    });
  };

  const autoExpandPending = (domainList: CustomDomain[]) => {
    const pendingIds = domainList
      .filter(d => d.status === 'pending' || d.status === 'pending_dns' || d.status === 'failed')
      .map(d => d.id);
    if (pendingIds.length > 0) {
setExpandedIds(prev => new Set([...Array.from(prev), ...pendingIds]));
    }
  };

  const copyToClipboard = (text: string, fieldKey: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(fieldKey);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const isDeleteConfirmValid = deleteConfirmText.toLowerCase() === 'delete';
  const atOrOverLimit = !tierLimit.unlimited && tierLimit.current >= tierLimit.limit;
  const overLimit = !tierLimit.unlimited && tierLimit.current > tierLimit.limit;

  const getStatusBadge = (status: string) => {
    const config: Record<string, { label: string; bgColor: string; textColor: string; icon: React.ReactNode }> = {
      active: {
        label: 'Active',
        bgColor: theme.colors.successBg,
        textColor: theme.colors.success,
        icon: <CheckCircle size={12} strokeWidth={2} />,
      },
      pending: {
        label: 'Pending DNS',
        bgColor: '#fef3c7',
        textColor: '#92400e',
        icon: <Clock size={12} strokeWidth={2} />,
      },
      pending_dns: {
        label: 'Pending DNS',
        bgColor: '#fef3c7',
        textColor: '#92400e',
        icon: <Clock size={12} strokeWidth={2} />,
      },
      failed: {
        label: 'Failed',
        bgColor: theme.colors.errorBg,
        textColor: theme.colors.error,
        icon: <AlertTriangle size={12} strokeWidth={2} />,
      },
      deleting: {
        label: 'Deleting...',
        bgColor: theme.colors.bgAlt,
        textColor: theme.colors.textMuted,
        icon: <Loader2 size={12} strokeWidth={2} style={{ animation: 'spin 1s linear infinite' }} />,
      },
    };
    const cfg = config[status] || {
      label: status,
      bgColor: theme.colors.bgAlt,
      textColor: theme.colors.textMuted,
      icon: null,
    };
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          padding: '4px 10px',
          fontSize: '0.75rem',
          fontWeight: 600,
          borderRadius: theme.radius.sm,
          backgroundColor: cfg.bgColor,
          color: cfg.textColor,
        }}
      >
        {cfg.icon}
        {cfg.label}
      </span>
    );
  };

  const CopyButton: React.FC<{ text: string; fieldKey: string }> = ({ text, fieldKey }) => {
    const isCopied = copiedField === fieldKey;
    const [hover, setHover] = useState(false);

    return (
      <button
        onClick={() => copyToClipboard(text, fieldKey)}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        title={isCopied ? 'Copied!' : 'Copy'}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '28px',
          height: '28px',
          backgroundColor: isCopied ? theme.colors.successBg : hover ? theme.colors.primaryLight : theme.colors.bgAlt,
          border: 'none',
          borderRadius: theme.radius.sm,
          cursor: 'pointer',
          color: isCopied ? theme.colors.success : hover ? theme.colors.primary : theme.colors.textTertiary,
          transition: 'all 0.15s ease',
          flexShrink: 0,
        }}
      >
        {isCopied ? <Check size={14} strokeWidth={2} /> : <Copy size={14} strokeWidth={1.5} />}
      </button>
    );
  };

  const DeleteButton: React.FC<{ onClick: () => void }> = ({ onClick }) => {
    const [hovered, setHovered] = useState(false);

    return (
      <button
        onClick={onClick}
        title="Delete domain"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '32px',
          height: '32px',
          backgroundColor: hovered ? theme.colors.errorBg : theme.colors.bgAlt,
          border: 'none',
          borderRadius: theme.radius.sm,
          cursor: 'pointer',
          color: hovered ? theme.colors.error : theme.colors.textTertiary,
          transition: 'all 0.15s ease',
          flexShrink: 0,
        }}
      >
        <Trash2 size={14} strokeWidth={1.5} />
      </button>
    );
  };

  // Error state
  if (error && !loading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px',
          backgroundColor: theme.colors.errorBg,
          border: `1px solid ${theme.colors.error}`,
          borderRadius: theme.radius.md,
          padding: '16px',
        }}
      >
        <AlertTriangle size={20} strokeWidth={1.5} color={theme.colors.error} style={{ flexShrink: 0 }} />
        <div style={{ flex: 1 }}>
          <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: 500, color: theme.colors.error }}>{error}</p>
        </div>
        <button
          onClick={onRefresh}
          style={{
            padding: '6px 12px',
            backgroundColor: theme.colors.error,
            color: '#ffffff',
            border: 'none',
            borderRadius: theme.radius.sm,
            fontSize: '0.8125rem',
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div
      style={{
        fontFamily: theme.fontSans,
        color: theme.colors.text,
      }}
    >
      {/* Header Section */}
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div
            style={{
              width: '48px',
              height: '48px',
              borderRadius: theme.radius.lg,
              background: `linear-gradient(135deg, ${theme.colors.primary} 0%, #818cf8 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 6px -1px rgba(79, 70, 229, 0.3)',
            }}
          >
            <Globe size={24} strokeWidth={1.5} color="#ffffff" />
          </div>
          <div>
            <h1
              style={{
                margin: 0,
                fontSize: '1.5rem',
                fontWeight: 700,
                letterSpacing: '-0.02em',
                color: theme.colors.text,
              }}
            >
              Custom Domains
            </h1>
	   <p
              style={{
                margin: 0,
                fontSize: '0.875rem',
                color: theme.colors.textTertiary,
              }}
            >
              {!tierLimit.unlimited
                ? `${tierLimit.current} of ${tierLimit.limit} custom domain${tierLimit.limit !== 1 ? 's' : ''} used`
                : 'Workspace — Configure your own URLs'}
            </p>
          </div>
        </div>

        {domains.length > 0 && (
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            onMouseEnter={() => setRefreshBtnHover(true)}
            onMouseLeave={() => setRefreshBtnHover(false)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 14px',
              backgroundColor: refreshing ? theme.colors.bgAlt : refreshBtnHover ? theme.colors.bgAlt : theme.colors.bg,
              color: theme.colors.textMuted,
              border: `1px solid ${theme.colors.border}`,
              borderRadius: theme.radius.md,
              fontSize: '0.875rem',
              fontWeight: 500,
              cursor: refreshing ? 'not-allowed' : 'pointer',
              transition: 'all 0.15s ease',
            }}
          >
            {refreshing ? (
              <Loader2 size={16} strokeWidth={2} style={{ animation: 'spin 1s linear infinite' }} />
            ) : (
              <RefreshCw size={16} strokeWidth={1.5} />
            )}
            {refreshing ? 'Refreshing...' : 'Refresh Status'}
          </button>
        )}
      </div>

{/* Tier Limit Banners */}
      {overLimit && (
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px',
            padding: '14px 20px',
            backgroundColor: theme.colors.errorBg,
            border: `1px solid ${theme.colors.error}`,
            borderRadius: theme.radius.lg,
            marginBottom: '24px',
          }}
        >
          <AlertTriangle size={18} strokeWidth={1.5} color={theme.colors.error} style={{ flexShrink: 0, marginTop: '1px' }} />
          <span style={{ fontSize: '0.875rem', color: theme.colors.error, lineHeight: 1.5 }}>
            <strong>Over limit:</strong> You have {tierLimit.current} custom domain{tierLimit.current !== 1 ? 's' : ''} but your plan only allows {tierLimit.limit}. Please delete {tierLimit.current - tierLimit.limit} domain{(tierLimit.current - tierLimit.limit) !== 1 ? 's' : ''} to come back into compliance.
          </span>
        </div>
      )}
      {!overLimit && atOrOverLimit && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '14px 20px',
            backgroundColor: theme.colors.errorBg,
            border: `1px solid ${theme.colors.error}`,
            borderRadius: theme.radius.lg,
            marginBottom: '24px',
          }}
        >
          <AlertTriangle size={18} strokeWidth={1.5} color={theme.colors.error} style={{ flexShrink: 0 }} />
          <span style={{ fontSize: '0.875rem', color: theme.colors.error }}>
            Custom domain limit reached ({tierLimit.current}/{tierLimit.limit}). Upgrade your plan or delete a domain to add more.
          </span>
        </div>
      )}

      {/* Add Domain Card */}
      <div
        style={{
          backgroundColor: theme.colors.bg,
          borderRadius: theme.radius.lg,
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: `1px solid ${theme.colors.border}`,
          padding: '24px',
          marginBottom: '24px',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '20px',
            paddingBottom: '16px',
            borderBottom: `1px solid ${theme.colors.border}`,
          }}
        >
          <div
            style={{
              width: '32px',
              height: '32px',
              borderRadius: theme.radius.md,
              backgroundColor: theme.colors.primaryLight,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Plus size={18} strokeWidth={2} color={theme.colors.primary} />
          </div>
          <span style={{ fontSize: '0.9375rem', fontWeight: 600, color: theme.colors.text }}>Add New Domain</span>
        </div>

        <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <label
              style={{
                display: 'block',
                fontSize: '0.6875rem',
                fontWeight: 600,
                color: theme.colors.textMuted,
                marginBottom: '8px',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              Domain Name
            </label>
            <input
              type="text"
              placeholder="dashboard.yourcompany.com"
              value={newDomain}
onChange={(e) => { setNewDomain(e.target.value.toLowerCase()); setAddError(null); }}
              onKeyPress={(e) => e.key === 'Enter' && handleAddDomain()}
              style={{
                width: '100%',
                padding: '10px 12px',
                backgroundColor: theme.colors.bg,
                border: `1px solid ${theme.colors.border}`,
                borderRadius: theme.radius.sm,
                fontSize: '0.875rem',
                color: theme.colors.text,
                fontFamily: theme.fontMono,
                fontWeight: 500,
                outline: 'none',
                transition: 'all 0.15s ease',
                boxSizing: 'border-box',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = theme.colors.primary;
                e.target.style.boxShadow = `0 0 0 3px ${theme.colors.primaryLight}`;
              }}
              onBlur={(e) => {
                e.target.style.borderColor = theme.colors.border;
                e.target.style.boxShadow = 'none';
              }}
            />
{addError ? (
              <p style={{ margin: '8px 0 0', fontSize: '0.8125rem', color: theme.colors.error, display: 'flex', alignItems: 'center', gap: '4px' }}>
                <AlertTriangle size={13} strokeWidth={2} style={{ flexShrink: 0 }} />
                {addError}
              </p>
            ) : (
              <p style={{ margin: '8px 0 0', fontSize: '0.8125rem', color: theme.colors.textTertiary }}>
                Subdomains recommended (e.g., app.yoursite.com)
              </p>
            )}
          </div>
          <div style={{ paddingTop: '28px' }}>
            <button
              onClick={handleAddDomain}
	      disabled={adding || !newDomain.trim() || atOrOverLimit}
              onMouseEnter={() => setAddBtnHover(true)}
              onMouseLeave={() => setAddBtnHover(false)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                padding: '10px 24px',
                minWidth: '140px',
		backgroundColor:
                  adding || !newDomain.trim() || atOrOverLimit
                    ? theme.colors.border
                    : addBtnHover
                      ? theme.colors.primaryHover
                      : theme.colors.primary,
                color: adding || !newDomain.trim() || atOrOverLimit ? theme.colors.textMuted : '#ffffff',
                border: 'none',
                borderRadius: theme.radius.md,
                fontSize: '0.875rem',
                fontWeight: 500,
                cursor: adding || !newDomain.trim() || atOrOverLimit ? 'not-allowed' : 'pointer',
                transition: 'all 0.15s ease',
              }}
            >
            {adding ? (
                <Loader2 size={16} strokeWidth={2} style={{ animation: 'spin 1s linear infinite' }} />
              ) : atOrOverLimit ? (
                <Lock size={16} strokeWidth={2} />
              ) : (
                <Plus size={16} strokeWidth={2} />
              )}
              {adding ? 'Adding...' : atOrOverLimit ? 'Limit Reached' : 'Add Domain'}
            </button>
          </div>
        </div>
      </div>

      {/* DNS Instructions Card */}
      <div
        style={{
          backgroundColor: theme.colors.bg,
          borderRadius: theme.radius.lg,
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: `1px solid ${theme.colors.border}`,
          borderLeft: `4px solid ${theme.colors.primary}`,
          padding: '24px',
          marginBottom: '24px',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            marginBottom: '12px',
          }}
        >
          <Info size={18} strokeWidth={1.5} color={theme.colors.primary} />
          <span style={{ fontSize: '0.9375rem', fontWeight: 600, color: theme.colors.text }}>
            DNS Configuration Required
          </span>
        </div>

        <p style={{ fontSize: '0.875rem', color: theme.colors.textMuted, marginBottom: '12px', lineHeight: 1.6, marginTop: 0 }}>
          After adding a domain, you'll need to configure <strong>two CNAME records</strong> at your DNS provider:
        </p>

        <ol style={{ fontSize: '0.875rem', color: theme.colors.textMuted, margin: '0 0 12px 0', paddingLeft: '20px', lineHeight: 1.8 }}>
          <li><strong>Main Domain</strong> — Routes traffic to your workspace</li>
          <li><strong>SSL Certificate</strong> — Enables HTTPS (required for security)</li>
        </ol>

        <p
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '0.8125rem',
            color: theme.colors.textTertiary,
            marginTop: '16px',
            marginBottom: 0,
            paddingTop: '12px',
            borderTop: `1px solid ${theme.colors.border}`,
          }}
        >
          <Clock size={14} strokeWidth={1.5} />
          DNS changes typically propagate within 5-60 minutes. We automatically verify your configuration every few minutes.
        </p>
      </div>

      {/* Domains List */}
      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '96px 0', gap: '12px' }}>
          <Loader2 size={32} strokeWidth={2} color={theme.colors.primary} style={{ animation: 'spin 1s linear infinite' }} />
          <span style={{ color: theme.colors.textTertiary, fontSize: '0.875rem' }}>Loading domains...</span>
        </div>
      ) : domains.length === 0 ? (
        <div
          style={{
            backgroundColor: theme.colors.bg,
            borderRadius: theme.radius.lg,
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            border: `1px solid ${theme.colors.border}`,
            padding: '64px',
            textAlign: 'center',
          }}
        >
          <div
            style={{
              width: '64px',
              height: '64px',
              borderRadius: theme.radius.lg,
              backgroundColor: theme.colors.primaryLight,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
            }}
          >
            <Globe size={28} strokeWidth={1.5} color={theme.colors.primary} />
          </div>
          <h3 style={{ margin: '0 0 8px', fontSize: '1.0625rem', color: theme.colors.text, fontWeight: 600 }}>
            No custom domains yet
          </h3>
          <p style={{ margin: 0, fontSize: '0.875rem', color: theme.colors.textTertiary }}>
            Add your first custom domain above to get started
          </p>
        </div>
      ) : (
        <div
          style={{
            backgroundColor: theme.colors.bg,
            borderRadius: theme.radius.lg,
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            border: `1px solid ${theme.colors.border}`,
            overflow: 'hidden',
          }}
        >
          {domains.map((domain, index) => {
const isExpanded = expandedIds.has(domain.id);
            const isPending = domain.status === 'pending' || domain.status === 'pending_dns';
            const isActive = domain.status === 'active';
            const dnsInstructions = domain.dns_instructions || {} as any;
            const mainCname = dnsInstructions.main_cname || {} as any;
            const acmeDelegation = dnsInstructions.acme_delegation;

            return (
              <div key={domain.id} style={{ borderBottom: index < domains.length - 1 ? `1px solid ${theme.colors.border}` : 'none' }}>
                {/* Main Row */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '16px 20px',
                    gap: '16px',
                    backgroundColor: isExpanded ? theme.colors.bgAlt : hoveredRow === domain.id ? theme.colors.bgAlt : theme.colors.bg,
                    transition: 'background-color 0.1s ease',
                  }}
                  onMouseEnter={() => setHoveredRow(domain.id)}
                  onMouseLeave={() => setHoveredRow(null)}
                >
                  {/* Expand Button */}
                  <button
                    onClick={() => handleExpandClick(domain.id)}
                    title={isExpanded ? 'Collapse' : 'View DNS configuration'}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '32px',
                      height: '32px',
                      backgroundColor: isExpanded ? theme.colors.border : 'transparent',
                      border: 'none',
                      borderRadius: theme.radius.sm,
                      cursor: 'pointer',
                      color: theme.colors.textTertiary,
                      transition: 'all 0.15s ease',
                      flexShrink: 0,
                    }}
                  >
                    <ChevronDown
                      size={16}
                      strokeWidth={1.5}
                      style={{
                        transition: 'transform 0.15s ease',
                        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)',
                      }}
                    />
                  </button>

{/* Domain Name */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <span
                      style={{
                        fontWeight: 600,
                        color: theme.colors.text,
                        fontSize: '0.875rem',
                        fontFamily: theme.fontMono,
                      }}
                    >
                      {domain.domain}
                    </span>
                    {isPending && (
                      <span
                        style={{
                          marginLeft: '10px',
                          fontSize: '0.6875rem',
                          fontWeight: 600,
                          color: '#92400e',
                          backgroundColor: '#fef3c7',
                          padding: '2px 8px',
                          borderRadius: theme.radius.sm,
                          fontFamily: theme.fontSans,
                          verticalAlign: 'middle',
                        }}
                      >
                        ↓ DNS setup required
                      </span>
                    )}
                  </div>

                  {/* Status Badge */}
                  <div style={{ flexShrink: 0 }}>{getStatusBadge(domain.status)}</div>

                  {/* Delete Button */}
                  <DeleteButton onClick={() => openDeleteModal(domain)} />
                </div>

{/* Expanded Details */}
                {isExpanded && (
                  <div
                    style={{
                      padding: '24px',
                      backgroundColor: theme.colors.bgAlt,
                      borderTop: `1px dashed ${theme.colors.border}`,
                    }}
                  >
                    {/* Section Header */}
                    {(isPending || domain.status === 'failed') && (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          padding: '14px 16px',
                          backgroundColor: '#fffbeb',
                          border: '1px solid #fcd34d',
                          borderRadius: theme.radius.md,
                          marginBottom: '20px',
                        }}
                      >
                        <Clock size={16} strokeWidth={1.5} color="#92400e" style={{ flexShrink: 0 }} />
                        <div>
                          <p style={{ margin: 0, fontWeight: 600, fontSize: '0.875rem', color: '#92400e' }}>
                            DNS Setup Required
                          </p>
                          <p style={{ margin: '2px 0 0 0', fontSize: '0.8125rem', color: '#92400e', opacity: 0.85 }}>
                            Log in to your DNS provider (GoDaddy, Namecheap, Cloudflare, etc.) and add both CNAME records below. Then click <strong>Refresh Status</strong> — SSL is issued automatically once DNS is verified.
                          </p>
                        </div>
                      </div>
                    )}

                    {isActive && (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          padding: '14px 16px',
                          backgroundColor: theme.colors.successBg,
                          border: '1px solid #86efac',
                          borderRadius: theme.radius.md,
                          marginBottom: '20px',
                        }}
                      >
                        <CheckCircle size={16} strokeWidth={1.5} color={theme.colors.success} style={{ flexShrink: 0 }} />
                        <p style={{ margin: 0, fontWeight: 500, fontSize: '0.875rem', color: theme.colors.success }}>
                          <strong>Fully configured</strong> — Your domain is live and secured with SSL.
                        </p>
                      </div>
                    )}

                    {domain.validation_errors && (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '10px',
                          padding: '14px 16px',
                          backgroundColor: theme.colors.errorBg,
                          border: '1px solid #feb2b2',
                          borderRadius: theme.radius.md,
                          marginBottom: '20px',
                        }}
                      >
                        <AlertTriangle size={16} strokeWidth={1.5} color={theme.colors.error} style={{ flexShrink: 0, marginTop: '1px' }} />
                        <div>
                          <p style={{ margin: 0, fontWeight: 600, fontSize: '0.875rem', color: theme.colors.error }}>Validation Error</p>
                          <p style={{ margin: '4px 0 0 0', fontSize: '0.8125rem', color: theme.colors.error }}>{domain.validation_errors}</p>
                        </div>
                      </div>
                    )}

                    {/* DNS Records */}
                    <p style={{ margin: '0 0 12px 0', fontSize: '0.8125rem', fontWeight: 600, color: theme.colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      DNS Records to Add
                    </p>

                    {/* Record 1 — Main CNAME */}
                    <div
                      style={{
                        backgroundColor: theme.colors.bg,
                        border: `1px solid ${theme.colors.border}`,
                        borderRadius: theme.radius.md,
                        overflow: 'hidden',
                        marginBottom: '12px',
                      }}
                    >
                      {/* Record Header */}
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '10px 16px',
                          backgroundColor: theme.colors.primaryLight,
                          borderBottom: `1px solid ${theme.colors.border}`,
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span
                            style={{
                              padding: '2px 8px',
                              backgroundColor: theme.colors.primary,
                              color: '#fff',
                              borderRadius: theme.radius.sm,
                              fontSize: '0.6875rem',
                              fontWeight: 700,
                              fontFamily: theme.fontMono,
                              letterSpacing: '0.05em',
                            }}
                          >
                            CNAME
                          </span>
                          <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: theme.colors.text }}>Record 1 — Main Domain</span>
                        </div>
                        <span style={{ fontSize: '0.75rem', color: theme.colors.textTertiary }}>Routes traffic to your workspace</span>
                      </div>

                      {/* Record Fields */}
                      <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {/* Host */}
                        <div>
                          <p style={{ margin: '0 0 6px 0', fontSize: '0.75rem', fontWeight: 600, color: theme.colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                            Host / Name
                          </p>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <code
                              style={{
                                flex: 1,
                                display: 'block',
                                padding: '10px 12px',
                                backgroundColor: theme.colors.bgAlt,
                                border: `1px solid ${theme.colors.border}`,
                                borderRadius: theme.radius.sm,
                                fontFamily: theme.fontMono,
                                fontSize: '0.875rem',
                                fontWeight: 600,
                                color: theme.colors.text,
                                wordBreak: 'break-all',
                              }}
                            >
                              {mainCname.host || domain.domain}
                            </code>
                            <CopyButton text={mainCname.host || domain.domain} fieldKey={`main-host-${domain.id}`} />
                          </div>
                          <p style={{ margin: '5px 0 0 0', fontSize: '0.75rem', color: theme.colors.textTertiary }}>
                            Some providers only want the subdomain part: <code style={{ fontFamily: theme.fontMono }}>{(mainCname.host || domain.domain).split('.')[0]}</code>
                          </p>
                        </div>

                        {/* Value */}
                        <div>
                          <p style={{ margin: '0 0 6px 0', fontSize: '0.75rem', fontWeight: 600, color: theme.colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                            Points To / Value
                          </p>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <code
                              style={{
                                flex: 1,
                                display: 'block',
                                padding: '10px 12px',
                                backgroundColor: theme.colors.primaryLight,
                                border: `1px solid ${theme.colors.primary}`,
                                borderRadius: theme.radius.sm,
                                fontFamily: theme.fontMono,
                                fontSize: '0.875rem',
                                fontWeight: 600,
                                color: theme.colors.primary,
                                wordBreak: 'break-all',
                              }}
                            >
                              {mainCname.value || 'hosted.dashboardfox.app'}
                            </code>
                            <CopyButton text={mainCname.value || 'hosted.dashboardfox.app'} fieldKey={`main-target-${domain.id}`} />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Record 2 — ACME / SSL */}
                    {acmeDelegation && (
                      <div
                        style={{
                          backgroundColor: theme.colors.bg,
                          border: `1px solid ${theme.colors.border}`,
                          borderRadius: theme.radius.md,
                          overflow: 'hidden',
                          marginBottom: '16px',
                        }}
                      >
                        {/* Record Header */}
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '10px 16px',
                            backgroundColor: theme.colors.primaryLight,
                            borderBottom: `1px solid ${theme.colors.border}`,
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span
                              style={{
                                padding: '2px 8px',
                                backgroundColor: theme.colors.primary,
                                color: '#fff',
                                borderRadius: theme.radius.sm,
                                fontSize: '0.6875rem',
                                fontWeight: 700,
                                fontFamily: theme.fontMono,
                                letterSpacing: '0.05em',
                              }}
                            >
                              CNAME
                            </span>
                            <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: theme.colors.text }}>Record 2 — SSL Certificate</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Lock size={12} strokeWidth={2} color={theme.colors.textTertiary} />
                            <span style={{ fontSize: '0.75rem', color: theme.colors.textTertiary }}>Required for HTTPS</span>
                          </div>
                        </div>

                        {/* Record Fields */}
                        <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                          {/* Host */}
                          <div>
                            <p style={{ margin: '0 0 6px 0', fontSize: '0.75rem', fontWeight: 600, color: theme.colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                              Host / Name
                            </p>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <code
                                style={{
                                  flex: 1,
                                  display: 'block',
                                  padding: '10px 12px',
                                  backgroundColor: theme.colors.bgAlt,
                                  border: `1px solid ${theme.colors.border}`,
                                  borderRadius: theme.radius.sm,
                                  fontFamily: theme.fontMono,
                                  fontSize: '0.875rem',
                                  fontWeight: 600,
                                  color: theme.colors.text,
                                  wordBreak: 'break-all',
                                }}
                              >
                                {acmeDelegation.host}
                              </code>
                              <CopyButton text={acmeDelegation.host} fieldKey={`acme-host-${domain.id}`} />
                            </div>
                            <p style={{ margin: '5px 0 0 0', fontSize: '0.75rem', color: theme.colors.textTertiary }}>
                              Some providers only want the subdomain part: <code style={{ fontFamily: theme.fontMono }}>{acmeDelegation.host.replace(`.${domain.domain.split('.').slice(-2).join('.')}`, '')}</code>
                            </p>
                          </div>

                          {/* Value */}
                          <div>
                            <p style={{ margin: '0 0 6px 0', fontSize: '0.75rem', fontWeight: 600, color: theme.colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                              Points To / Value
                            </p>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <code
                                style={{
                                  flex: 1,
                                  display: 'block',
                                  padding: '10px 12px',
                                  backgroundColor: theme.colors.primaryLight,
                                  border: `1px solid ${theme.colors.primary}`,
                                  borderRadius: theme.radius.sm,
                                  fontFamily: theme.fontMono,
                                  fontSize: '0.875rem',
                                  fontWeight: 600,
                                  color: theme.colors.primary,
                                  wordBreak: 'break-all',
                                }}
                              >
                                {acmeDelegation.value}
                              </code>
                              <CopyButton text={acmeDelegation.value} fieldKey={`acme-target-${domain.id}`} />
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Propagation note */}
                    {isPending && (
                      <p
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          margin: 0,
                          fontSize: '0.8125rem',
                          color: theme.colors.textTertiary,
                          paddingTop: '4px',
                        }}
                      >
                        <Clock size={13} strokeWidth={1.5} style={{ flexShrink: 0 }} />
                        DNS changes typically propagate in 5–30 minutes. We check automatically every few minutes.
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal.open && deleteModal.domain && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) closeDeleteModal();
          }}
        >
          <div
            style={{
              backgroundColor: theme.colors.bg,
              borderRadius: theme.radius.lg,
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              border: `1px solid ${theme.colors.border}`,
              maxWidth: '480px',
              width: '90%',
              overflow: 'hidden',
              fontFamily: theme.fontSans,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div
              style={{
                padding: '20px 24px',
                borderBottom: `1px solid ${theme.colors.border}`,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: theme.radius.md,
                    backgroundColor: theme.colors.errorBg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <AlertTriangle size={20} strokeWidth={1.5} color={theme.colors.error} />
                </div>
                <div>
                  <h2
                    style={{
                      fontSize: '1.125rem',
                      fontWeight: 600,
                      color: theme.colors.text,
                      margin: 0,
                    }}
                  >
                    Delete Domain
                  </h2>
                  <p
                    style={{
                      fontSize: '0.875rem',
                      color: theme.colors.error,
                      margin: '2px 0 0 0',
                      fontWeight: 500,
                    }}
                  >
                    This action cannot be undone
                  </p>
                </div>
              </div>
              <button
                onClick={closeDeleteModal}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '8px',
                  cursor: 'pointer',
                  borderRadius: theme.radius.sm,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: theme.colors.textMuted,
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = theme.colors.bgAlt;
                  e.currentTarget.style.color = theme.colors.text;
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = theme.colors.textMuted;
                }}
              >
                <X size={20} strokeWidth={1.5} />
              </button>
            </div>

            {/* Content */}
            <div style={{ padding: '24px' }}>
              <div
                style={{
                  backgroundColor: theme.colors.bgAlt,
                  border: `1px solid ${theme.colors.border}`,
                  borderRadius: theme.radius.md,
                  padding: '16px',
                  marginBottom: '20px',
                }}
              >
                <p
                  style={{
                    fontSize: '0.6875rem',
                    fontWeight: 600,
                    color: theme.colors.textMuted,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    margin: '0 0 6px 0',
                  }}
                >
                  Domain to Delete
                </p>
                <p
                  style={{
                    fontSize: '1.0625rem',
                    fontWeight: 600,
                    color: theme.colors.text,
                    margin: 0,
                    fontFamily: theme.fontMono,
                  }}
                >
                  {deleteModal.domain.domain}
                </p>
              </div>

              <p
                style={{
                  fontSize: '0.875rem',
                  color: theme.colors.textMuted,
                  margin: '0 0 16px 0',
                  lineHeight: 1.5,
                }}
              >
                This will permanently remove the domain and all associated SSL certificates. The domain will no longer route to your workspace.
              </p>

              <div>
                <label
                  style={{
                    display: 'block',
                    fontSize: '0.875rem',
                    color: theme.colors.text,
                    marginBottom: '8px',
                  }}
                >
                  Type{' '}
                  <strong
                    style={{
                      fontFamily: theme.fontMono,
                      backgroundColor: theme.colors.bgAlt,
                      padding: '2px 6px',
                      borderRadius: '4px',
                    }}
                  >
                    DELETE
                  </strong>{' '}
                  to confirm:
                </label>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="DELETE"
                  disabled={deleting}
                  autoFocus
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    backgroundColor: theme.colors.bg,
                    border: `1px solid ${deleteConfirmText !== '' && !isDeleteConfirmValid ? theme.colors.error : theme.colors.border}`,
                    borderRadius: theme.radius.sm,
                    fontSize: '0.875rem',
                    color: theme.colors.text,
                    fontFamily: theme.fontMono,
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                  onFocus={(e) => {
                    if (deleteConfirmText === '' || isDeleteConfirmValid) {
                      e.target.style.borderColor = theme.colors.primary;
                      e.target.style.boxShadow = `0 0 0 3px ${theme.colors.primaryLight}`;
                    }
                  }}
                  onBlur={(e) => {
                    if (deleteConfirmText === '' || isDeleteConfirmValid) {
                      e.target.style.borderColor = theme.colors.border;
                    }
                    e.target.style.boxShadow = 'none';
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && isDeleteConfirmValid) {
                      handleDeleteDomain();
                    }
                  }}
                />
                {deleteConfirmText !== '' && !isDeleteConfirmValid && (
                  <p style={{ fontSize: '0.8125rem', color: theme.colors.error, margin: '6px 0 0 0' }}>Please type DELETE to confirm</p>
                )}
              </div>
            </div>

            {/* Footer */}
            <div
              style={{
                padding: '16px 24px',
                borderTop: `1px solid ${theme.colors.border}`,
                backgroundColor: theme.colors.bgAlt,
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '12px',
              }}
            >
              <button
                onClick={closeDeleteModal}
                disabled={deleting}
                onMouseEnter={() => setHoveredButton('cancelDelete')}
                onMouseLeave={() => setHoveredButton(null)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: hoveredButton === 'cancelDelete' && !deleting ? theme.colors.bgAlt : theme.colors.bg,
                  color: hoveredButton === 'cancelDelete' && !deleting ? theme.colors.text : theme.colors.textTertiary,
                  border: `1px solid ${theme.colors.border}`,
                  borderRadius: theme.radius.md,
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  cursor: deleting ? 'not-allowed' : 'pointer',
                  opacity: deleting ? 0.5 : 1,
                  transition: 'all 0.15s',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteDomain}
                disabled={!isDeleteConfirmValid || deleting}
                onMouseEnter={() => setHoveredButton('confirmDelete')}
                onMouseLeave={() => setHoveredButton(null)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: !isDeleteConfirmValid || deleting ? '#fecaca' : hoveredButton === 'confirmDelete' ? '#b91c1c' : theme.colors.error,
                  color: !isDeleteConfirmValid || deleting ? '#f87171' : '#ffffff',
                  border: 'none',
                  borderRadius: theme.radius.md,
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  cursor: !isDeleteConfirmValid || deleting ? 'not-allowed' : 'pointer',
                  transition: 'background-color 0.15s',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                {deleting ? (
                  <>
                    <Loader2 size={16} strokeWidth={2} style={{ animation: 'spin 1s linear infinite' }} />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 size={16} strokeWidth={1.5} />
                    Delete Domain
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default CustomDomainsCore;
